
import {Component, Prop} from 'vue-property-decorator';
import FilterWidget from '@/components/reports-v3/components/codedWidgets/shared/FilterWidget.vue';
import AmountDisplayerFilterable from '@/components/reports-v3/components/filterables/AmountDisplayerFilterable.vue';
import BaseFilterable from '@/components/reports-v3/components/filterables/BaseFilterable.vue';
import LineChartFilterable from '@/components/reports-v3/components/filterables/LineChartFilterable.vue';
import PieChartFilterable from '@/components/reports-v3/components/filterables/PieChartFilterable.vue';
import StackedBarChart from '@/components/reports-v3/components/elements/charts/StackedBarChart.vue';
import DataInfo from '@/components/reports-v3/components/elements/DataInfo.vue';
import DataTable from 'primevue/datatable';
import Column from 'primevue/column';
import ColumnGroup from 'primevue/columngroup';
import Row from 'primevue/row';
import InputText from 'primevue/inputtext';
import Button from 'primevue/button';
import {FilterMatchMode} from 'primevue/api';
import {PermissionsGroup} from '@/store/models.def';
import {getTableName} from '@/worker/db/sqlHelpers';
import gdbx from '@/store/modules/gdbx';

interface DeptMonthly {
    project: string;
    month: number;
    sales: number;
    cost: number;
    gp: number;
}

interface BudgetMonthly {
    project: string;
    month: number;
    dr: number;
    cr: number;
}

const SUPPORT_LABEL = 'SUPPORT & TRAINING';

@Component({
    components: {
        AmountDisplayerFilterable,
        BaseFilterable,
        LineChartFilterable,
        PieChartFilterable,
        StackedBarChart,
        DataInfo,
        DataTable,
        Column,
        ColumnGroup,
        Row,
        InputText,
        Button,
    },
})
export default class DataEmpTargetDashboard extends FilterWidget {

    public get permissionIds(): PermissionsGroup[] {
        return ['dashboard'];
    }

    public get currencySymbol() {
        return gdbx.currencySymbol || 'RM';
    }

    public get selectedMetricLabel(): string {
        const found = this.metricOptions.find((m) => m.value === this.selectedMetric);
        return found ? found.label : 'Sales';
    }

    public get expensiveHook() {
        return JSON.stringify([this.selectedYear, this.selectedDepartment]);
    }

    public get filteredActuals(): DeptMonthly[] {
        if (this.selectedDepartment === 'all') {
            return this.actualData;
        }
        return this.actualData.filter((d) => d.project === this.selectedDepartment);
    }

    public get filteredBudgets(): BudgetMonthly[] {
        if (this.selectedDepartment === 'all') {
            return this.budgetData;
        }
        return this.budgetData.filter((d) => d.project === this.selectedDepartment);
    }

    public get filteredPrevYearActuals(): DeptMonthly[] {
        if (this.selectedDepartment === 'all') {
            return this.prevYearActualData;
        }
        return this.prevYearActualData.filter((d) => d.project === this.selectedDepartment);
    }

    public get hasBudgetData(): boolean {
        return this.budgetData.length > 0;
    }

    public get kpiSales() {
        const actual = this.sumActual('sales');
        const budget = this.sumBudget();
        const target = budget.cr;
        return {target, actual, percentage: target > 0 ? (actual / target) * 100 : 0};
    }

    public get kpiCost() {
        const actual = this.sumActual('cost');
        const budget = this.sumBudget();
        const target = budget.dr;
        return {target, actual, percentage: target > 0 ? (actual / target) * 100 : 0};
    }

    public get kpiGp() {
        const actual = this.sumActual('gp');
        const target = this.kpiSales.target - this.kpiCost.target;
        return {target, actual, percentage: target > 0 ? (actual / target) * 100 : 0};
    }

    public get costRevenueRatio(): string {
        const revenue = this.kpiSales.actual;
        return revenue > 0 ? ((this.kpiCost.actual / revenue) * 100).toFixed(1) : '0.0';
    }

    public get gpRevenueRatio(): string {
        const revenue = this.kpiSales.actual;
        return revenue > 0 ? ((this.kpiGp.actual / revenue) * 100).toFixed(1) : '0.0';
    }

    public get departmentTableItems() {
        const rows = this.projectList
            .filter((p) => this.selectedDepartment === 'all' || p === this.selectedDepartment)
            .map((project) => {
                const actuals = this.actualData.filter((r) => r.project === project);
                const budgets = this.budgetData.filter((r) => r.project === project);
                return this._buildDeptRow(project, actuals, budgets);
            });

        // Add TOTAL row
        if (rows.length > 1) {
            const totalActuals = this.filteredActuals;
            const totalBudgets = this.filteredBudgets;
            const totalRow = this._buildDeptRow('TOTAL', totalActuals, totalBudgets);
            totalRow._rowVariant = 'primary';
            rows.push(totalRow);
        }

        return rows;
    }

    public get deptChartXAxis(): string[] {
        return this.projectList;
    }

    public get deptChartColors(): string[] {
        if (this.hasBudgetData) {
            return ['#876BED', '#28a745'];
        }
        return ['#28a745'];
    }

    public get deptChartSeries(): any[] {
        const metric = this.selectedMetric as 'sales' | 'cost' | 'gp';
        const series: any[] = [];

        const actualData = this.projectList.map((project) => {
            const actuals = this.actualData.filter((r) => r.project === project);
            return actuals.reduce((a, r) => a + r[metric], 0);
        });

        if (this.hasBudgetData) {
            const targetData = this.projectList.map((project) => {
                const budgets = this.budgetData.filter((r) => r.project === project);
                if (metric === 'sales') {
                    return budgets.reduce((a, r) => a + r.cr, 0);
                } else if (metric === 'cost') {
                    return budgets.reduce((a, r) => a + r.dr, 0);
                } else {
                    return budgets.reduce((a, r) => a + (r.cr - r.dr), 0);
                }
            });
            series.push({
                name: 'Target',
                type: 'bar',
                barWidth: '30%',
                data: targetData,
            });
            series.push({
                name: 'Actual',
                type: 'bar',
                barWidth: '30%',
                data: actualData,
            });
        } else {
            series.push({
                name: 'Actual',
                type: 'bar',
                barWidth: '30%',
                data: actualData,
            });
        }

        return series;
    }

    public get deptChartTooltipFormatter() {
        return (params: any[]) => {
            let html = `<b>${params[0].name}</b><br/>`;
            let targetVal: number | null = null;
            let actualVal: number | null = null;
            for (const p of params) {
                const val = Math.ceil(p.value).toLocaleString('en-US');
                html += `${p.marker} ${p.seriesName}: ${val}<br/>`;
                if (p.seriesName === 'Target') { targetVal = p.value; }
                if (p.seriesName === 'Actual') { actualVal = p.value; }
            }
            if (targetVal !== null && actualVal !== null) {
                const toGo = targetVal > 0 ? targetVal - actualVal : 0;
                const toGoAbs = Math.ceil(Math.abs(toGo)) || 0;
                const toGoFormatted = toGoAbs.toLocaleString('en-US');
                const sign = toGo < 0 ? '-' : '';
                html += `<span style="display:inline-block;width:10px;height:10px;border-radius:50%;background:#f0ad4e;margin-right:5px;"></span> To Go: ${sign}${toGoFormatted}`;
            }
            return html;
        };
    }

    public get chartXAxis(): string[] {
        return ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    }

    public get chartColors(): string[] {
        if (this.hasBudgetData) {
            return ['#876BED', '#28a745'];
        }
        return ['#28a745'];
    }

    public get chartSeries(): any[] {
        const actuals = this.filteredActuals;
        const metric = this.selectedMetric as 'sales' | 'cost' | 'gp';

        // Build monthly actuals
        const actualMonthly: number[] = new Array(12).fill(0);
        for (const r of actuals) {
            if (r.month >= 1 && r.month <= 12) {
                actualMonthly[r.month - 1] += r[metric];
            }
        }

        // Accumulate actuals
        const actualCum: number[] = [];
        let aRunning = 0;
        for (let m = 0; m < 12; m++) {
            aRunning += actualMonthly[m];
            actualCum.push(aRunning);
        }

        const series: any[] = [];

        if (this.hasBudgetData) {
            // Use proportioned monthly targets
            const monthlyTargets = this.getMonthlyTargets(metric);
            const targetCum: number[] = [];
            let tRunning = 0;
            for (let m = 0; m < 12; m++) {
                tRunning += monthlyTargets[m];
                targetCum.push(tRunning);
            }

            series.push({
                name: 'Target (Cumulative)',
                type: 'line',
                data: targetCum,
                smooth: true,
                lineStyle: {width: 2, type: 'dashed'},
                areaStyle: {opacity: 0.05},
            });
        }

        series.push({
            name: 'Actual (Cumulative)',
            type: 'line',
            data: actualCum,
            smooth: true,
            lineStyle: {width: 3},
            areaStyle: {opacity: 0.15},
        });

        return series;
    }

    public get revenueDistributionData(): Array<{ name: string; value: number }> {
        return this.projectList.map((project) => {
            const actuals = this.actualData.filter((r) => r.project === project);
            const value = actuals.reduce((a, r) => a + r.sales, 0);
            return {name: project, value};
        }).filter((d) => d.value > 0);
    }

    public filterIds: string[] = [];

    @Prop({default: 2026}) public selectedYear!: number;
    @Prop({default: 'all'}) public selectedDepartment!: string;

    public selectedMetric: string = 'sales';

    public metricOptions = [
        {label: 'Net Revenue', value: 'sales'},
        {label: 'Cost', value: 'cost'},
        {label: 'Gross Profit', value: 'gp'},
    ];

    public deptFilter: any = {
        global: {value: null, matchMode: FilterMatchMode.CONTAINS},
    };

    // Data loaded from DuckDB
    public actualData: DeptMonthly[] = [];
    public budgetData: BudgetMonthly[] = [];
    public prevYearActualData: DeptMonthly[] = [];
    public projectList: string[] = [];
    public yearList: number[] = [];

    public async expensiveCalc() {
        const jobId = this.currentJobId;
        this.generateLoadingText(0.2, 0.3);

        const queryHead = await this._ensureTablesLoaded(['diyActualPerformance']);
        this.checkInterrupted(jobId);

        // Try loading budget table (may not exist yet if customer hasn't synced)
        let budgetLoaded = false;
        try {
            await this._ensureTablesLoaded(['diyGlobalBudget']);
            budgetLoaded = true;
        } catch (e) {
            // Budget table not available — continue without target data
        }
        this.checkInterrupted(jobId);
        this.generateLoadingText(0.3, 0.5);

        const actualTable = getTableName('diyActualPerformance');
        const budgetTable = budgetLoaded ? getTableName('diyGlobalBudget') : '';

        // Query distinct years for filter dropdown
        const yearResult = await queryHead.rawQuery(`
            SELECT DISTINCT TRY_CAST("DYEAR" AS INTEGER) as year
            FROM ${actualTable}
            ORDER BY year DESC
        `);
        this.checkInterrupted(jobId);

        this.yearList = yearResult.map((r: any) => Number(r.year)).filter((y: number) => y > 0);

        // Query current year and previous year actuals in parallel
        const prevYear = this.selectedYear - 1;
        const [actualResult, prevYearResult] = await Promise.all([
            queryHead.rawQuery(`
                SELECT
                    "PROJECT" as project,
                    TRY_CAST("DMONTH" AS INTEGER) as month,
                    SUM(CASE WHEN "ACCTYPE" IN ('SL', 'SA')
                        THEN TRY_CAST("LOCALCR" AS DOUBLE) - TRY_CAST("LOCALDR" AS DOUBLE)
                        ELSE 0 END) as sales,
                    SUM(CASE WHEN "ACCTYPE" = 'CO'
                        THEN TRY_CAST("LOCALDR" AS DOUBLE) - TRY_CAST("LOCALCR" AS DOUBLE)
                        ELSE 0 END) as cost
                FROM ${actualTable}
                WHERE TRY_CAST("DYEAR" AS INTEGER) = ${this.selectedYear}
                GROUP BY "PROJECT", "DMONTH"
                ORDER BY "PROJECT", "DMONTH"
            `),
            queryHead.rawQuery(`
                SELECT
                    "PROJECT" as project,
                    TRY_CAST("DMONTH" AS INTEGER) as month,
                    SUM(CASE WHEN "ACCTYPE" IN ('SL', 'SA')
                        THEN TRY_CAST("LOCALCR" AS DOUBLE) - TRY_CAST("LOCALDR" AS DOUBLE)
                        ELSE 0 END) as sales,
                    SUM(CASE WHEN "ACCTYPE" = 'CO'
                        THEN TRY_CAST("LOCALDR" AS DOUBLE) - TRY_CAST("LOCALCR" AS DOUBLE)
                        ELSE 0 END) as cost
                FROM ${actualTable}
                WHERE TRY_CAST("DYEAR" AS INTEGER) = ${prevYear}
                GROUP BY "PROJECT", "DMONTH"
                ORDER BY "PROJECT", "DMONTH"
            `),
        ]);

        let budgetResult: any[] = [];
        if (budgetLoaded) {
            budgetResult = await queryHead.rawQuery(`
                SELECT
                    "PROJECT" as project,
                    TRY_CAST("BMONTH" AS INTEGER) as month,
                    SUM(TRY_CAST("DR" AS DOUBLE)) as dr,
                    SUM(TRY_CAST("CR" AS DOUBLE)) as cr
                FROM ${budgetTable}
                WHERE TRY_CAST("BYEAR" AS INTEGER) = ${this.selectedYear}
                GROUP BY "PROJECT", "BMONTH"
                ORDER BY "PROJECT", "BMONTH"
            `);
        }
        this.checkInterrupted(jobId);
        this.generateLoadingText(0.6, 0.8);

        // Map and merge current year actuals
        const rawActuals = actualResult.map((r: any) => ({
            project: this._mapProjectName(String(r.project)),
            month: Number(r.month) || 0,
            sales: Number(r.sales) || 0,
            cost: Number(r.cost) || 0,
            gp: (Number(r.sales) || 0) - (Number(r.cost) || 0),
        }));
        this.actualData = this._mergeActuals(rawActuals);

        // Map and merge previous year actuals (for monthly proportioning)
        const rawPrevYear = prevYearResult.map((r: any) => ({
            project: this._mapProjectName(String(r.project)),
            month: Number(r.month) || 0,
            sales: Number(r.sales) || 0,
            cost: Number(r.cost) || 0,
            gp: (Number(r.sales) || 0) - (Number(r.cost) || 0),
        }));
        this.prevYearActualData = this._mergeActuals(rawPrevYear);

        // Map budget projects
        this.budgetData = budgetResult.map((r: any) => ({
            project: this._mapBudgetProject(String(r.project)),
            month: Number(r.month) || 0,
            dr: Number(r.dr) || 0,
            cr: Number(r.cr) || 0,
        }));

        // Derive project list from merged actuals (dynamic, not hardcoded)
        const projectSet = new Set<string>();
        for (const r of this.actualData) {
            if (r.project && r.project !== '----') {
                projectSet.add(r.project);
            }
        }
        this.projectList = Array.from(projectSet).sort();
        this.$emit('filterOptions', {years: this.yearList, projects: this.projectList});

        this.generateLoadingText(1);
        this.saveHistory('actualData', 'budgetData', 'prevYearActualData', 'projectList', 'yearList');
    }

    public sumActual(metric: 'sales' | 'cost' | 'gp'): number {
        return this.filteredActuals.reduce((acc, r) => acc + r[metric], 0);
    }

    public sumBudget(): { dr: number; cr: number } {
        return this.filteredBudgets.reduce((acc, r) => {
            acc.dr += r.dr;
            acc.cr += r.cr;
            return acc;
        }, {dr: 0, cr: 0});
    }

    // Compute monthly target proportions based on previous year actual distribution
    public getMonthlyTargets(metric: 'sales' | 'cost' | 'gp'): number[] {
        const budgets = this.filteredBudgets;
        const prevActuals = this.filteredPrevYearActuals;

        // Annual target
        let annualTarget = 0;
        if (metric === 'sales') {
            annualTarget = budgets.reduce((a, r) => a + r.cr, 0);
        } else if (metric === 'cost') {
            annualTarget = budgets.reduce((a, r) => a + r.dr, 0);
        } else {
            annualTarget = budgets.reduce((a, r) => a + (r.cr - r.dr), 0);
        }

        // Previous year monthly actuals
        const prevMonthly: number[] = new Array(12).fill(0);
        for (const r of prevActuals) {
            if (r.month >= 1 && r.month <= 12) {
                prevMonthly[r.month - 1] += r[metric];
            }
        }
        const prevTotal = prevMonthly.reduce((a, v) => a + v, 0);

        // Proportion monthly targets
        const monthlyTargets: number[] = new Array(12).fill(0);
        if (prevTotal > 0) {
            for (let m = 0; m < 12; m++) {
                monthlyTargets[m] = annualTarget * (prevMonthly[m] / prevTotal);
            }
        } else {
            // Fallback: spread evenly
            for (let m = 0; m < 12; m++) {
                monthlyTargets[m] = annualTarget / 12;
            }
        }
        return monthlyTargets;
    }

    public getProgressColor(pct: number): string {
        if (pct >= 100) {
            return '#28a745';
        }
        if (pct >= 70) {
            return '#ffc107';
        }
        return '#dc3545';
    }

    public getCostProgressColor(pct: number): string {
        if (pct >= 100) {
            return '#dc3545';
        }
        if (pct >= 85) {
            return '#ffc107';
        }
        return '#28a745';
    }

    public formatCurrency(value: number): string {
        if (value >= 1000000) {
            return this.currencySymbol + ' ' + (value / 1000000).toFixed(2) + 'M';
        }
        if (value >= 1000) {
            return this.currencySymbol + ' ' + (value / 1000).toFixed(1) + 'K';
        }
        return this.currencySymbol + ' ' + value.toFixed(0);
    }

    public thousandFormatter(x: number): string {
        return Math.ceil(x).toLocaleString('en-US');
    }

    public exportDeptCSV() {
        (this.$refs.deptTable as any).exportCSV();
    }

    public deptRowClass(data: any) {
        return data._rowVariant === 'primary' ? 'font-weight-bold' : '';
    }

    // Map raw project names: SUPPORT and TRAINING → 'Support & Training'
    private _mapProjectName(raw: string): string {
        if (raw === 'SUPPORT' || raw === 'TRAINING') {
            return SUPPORT_LABEL;
        }
        return raw;
    }

    // Merge and re-aggregate rows after project name mapping
    private _mergeActuals(rows: DeptMonthly[]): DeptMonthly[] {
        const map = new Map<string, DeptMonthly>();
        for (const r of rows) {
            const key = r.project + '_' + r.month;
            const existing = map.get(key);
            if (existing) {
                existing.sales += r.sales;
                existing.cost += r.cost;
                existing.gp += r.gp;
            } else {
                map.set(key, {project: r.project, month: r.month, sales: r.sales, cost: r.cost, gp: r.gp});
            }
        }
        return Array.from(map.values());
    }

    // Map budget project: SUPPORT → 'Support & Training'
    private _mapBudgetProject(raw: string): string {
        if (raw === 'SUPPORT') {
            return SUPPORT_LABEL;
        }
        return raw;
    }

    private _buildDeptRow(label: string, actuals: DeptMonthly[], budgets: BudgetMonthly[]): any {
        const salesActual = actuals.reduce((a, r) => a + r.sales, 0);
        const costActual = actuals.reduce((a, r) => a + r.cost, 0);
        const gpActual = salesActual - costActual;

        const salesTarget = budgets.reduce((a, r) => a + r.cr, 0);
        const costTarget = budgets.reduce((a, r) => a + r.dr, 0);
        const gpTarget = salesTarget - costTarget;

        const row: any = {department: label};

        if (this.hasBudgetData) {
            row.salesTarget = this.thousandFormatter(salesTarget);
        }
        row.salesActual = this.thousandFormatter(salesActual);
        if (this.hasBudgetData) {
            row.salesPct = (salesTarget > 0 ? (salesActual / salesTarget) * 100 : 0).toFixed(1) + '%';
            row.costTarget = this.thousandFormatter(costTarget);
        }
        row.costActual = this.thousandFormatter(costActual);
        if (this.hasBudgetData) {
            row.costPct = (costTarget > 0 ? (costActual / costTarget) * 100 : 0).toFixed(1) + '%';
            row.gpTarget = this.thousandFormatter(gpTarget);
        }
        row.gpActual = this.thousandFormatter(gpActual);
        if (this.hasBudgetData) {
            row.gpPct = (gpTarget > 0 ? (gpActual / gpTarget) * 100 : 0).toFixed(1) + '%';
        }
        return row;
    }
}
